'use client'

import * as React from 'react'
import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from '@radix-ui/react-slot'
import { cx } from 'class-variance-authority'
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form'

import { Alert } from '@/components/atoms/Alert/alert.atom'
import { Label } from '@/components/atoms/Label/label.atom'

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName
}

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

export const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState, formState } = useFormContext()

  if (!fieldContext?.name) {
    throw new Error('useFormField should be used within <FormField>')
  }
  if (!itemContext) {
    throw new Error('useFormField should be used within <FormItem>')
  }

  const fieldState = getFieldState(fieldContext.name, formState)

  const { id, hasDescription, hasMessage } = itemContext

  return {
    id,
    hasDescription,
    hasMessage,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
  hasDescription: boolean
  hasMessage: boolean
  setHasDescription: React.Dispatch<React.SetStateAction<boolean>>
  setHasMessage: React.Dispatch<React.SetStateAction<boolean>>
}

const FormItemContext = React.createContext<FormItemContextValue | null>(null)

type FormItemProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string
}

const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(
  (props, ref) => {
    const { className, ...rest } = props
    const id = React.useId()
    const [hasDescription, setHasDescription] = React.useState(false)
    const [hasMessage, setHasMessage] = React.useState(false)
    const contextValue = React.useMemo(
      () => ({
        id,
        hasDescription,
        hasMessage,
        setHasDescription,
        setHasMessage,
      }),
      [id, hasDescription, hasMessage],
    )

    return (
      <FormItemContext.Provider value={contextValue}>
        <div ref={ref} className={cx('space-y-2', className)} {...rest} />
      </FormItemContext.Provider>
    )
  },
)
FormItem.displayName = 'FormItem'

type FormLabelProps = React.ComponentPropsWithoutRef<
  typeof LabelPrimitive.Root
> & { className?: string }

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  FormLabelProps
>((props, ref) => {
  const { className, ['aria-required']: ariaRequired, ...rest } = props
  const { error, formItemId } = useFormField()
  const isRequired = ariaRequired === true || ariaRequired === 'true'

  return (
    <Label
      ref={ref}
      className={cx(
        "font-normal text-slate-500 data-[required=true]:font-medium data-[required=true]:after:content-['_*']",
        error && 'text-destructive',
        className,
      )}
      htmlFor={formItemId}
      data-required={isRequired ? 'true' : undefined}
      {...rest}
    />
  )
})
FormLabel.displayName = 'FormLabel'

type FormControlProps = React.ComponentPropsWithoutRef<typeof Slot>

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  FormControlProps
>(({ ...rest }, ref) => {
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId,
    hasDescription,
    hasMessage,
  } = useFormField()

  const describedBy = [
    hasDescription ? formDescriptionId : null,
    hasMessage ? formMessageId : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={describedBy || undefined}
      aria-invalid={!!error}
      {...rest}
    />
  )
})
FormControl.displayName = 'FormControl'

type FormDescriptionProps = React.HTMLAttributes<HTMLParagraphElement> & {
  className?: string
}

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  FormDescriptionProps
>((props, ref) => {
  const { className, ...rest } = props
  const { formDescriptionId } = useFormField()
  const setHasDescription = React.useContext(FormItemContext)?.setHasDescription

  React.useEffect(() => {
    if (!setHasDescription) return
    setHasDescription(true)
    return () => setHasDescription(false)
  }, [setHasDescription])

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cx('text-sm text-slate-200', className)}
      {...rest}
    />
  )
})
FormDescription.displayName = 'FormDescription'

type FormMessageProps = React.HTMLAttributes<HTMLParagraphElement> & {
  className?: string
}

const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  (props, ref) => {
    const { className, children, ...rest } = props
    const { error, formMessageId } = useFormField()
    const setHasMessage = React.useContext(FormItemContext)?.setHasMessage
    const hasCustomBody = children !== undefined && children !== null && children !== ''
    const body = hasCustomBody ? children : error ? String(error?.message) : undefined
    const hasBody = Boolean(body)

    React.useEffect(() => {
      if (!setHasMessage) return
      setHasMessage(hasBody)
      return () => setHasMessage(false)
    }, [setHasMessage, hasBody])

    if (!body) {
      return null
    }

    return (
      <Alert.Root ref={ref} id={formMessageId} className={className} {...rest}>
        <Alert.Description>{body}</Alert.Description>
      </Alert.Root>
    )
  },
)
FormMessage.displayName = 'FormMessage'

export const Form: {
  Provider: typeof FormProvider
  Item: typeof FormItem
  Label: typeof FormLabel
  Control: typeof FormControl
  Description: typeof FormDescription
  Message: typeof FormMessage
  Field: typeof FormField
} = {
  Provider: FormProvider,
  Item: FormItem,
  Label: FormLabel,
  Control: FormControl,
  Description: FormDescription,
  Message: FormMessage,
  Field: FormField,
}
